import React, {useEffect, useMemo, useState} from "react";
import {Prompt} from "react-router-dom";
import {Button, CircularProgress, Divider, IconButton, Typography} from "@mui/material";
import {NavigateBefore as PrevIcon, NavigateNext as NextIcon} from "@mui/icons-material";
import {AccountAvatar, VerticalDivider} from "@atttomyx/react-components";
import {TypeForm, TypeRoleForm} from "../forms";
import {useFiltered} from "@atttomyx/shared-hooks";
import {internalTypeService} from "../../services";
import {arrays, forms, sorting, strings} from "@atttomyx/shared-utils";
import {verbiage} from "@atttomyx/shared-constants";
import "./editTypePage.css";

const isValid = (type) => strings.isNotBlank(type.id);

const toData = (type) => {
    return {
        title: type.title,
        description: type.description,
        valid: isValid(type),
    }
};

const toRoles = (type) => {
    const roles = type.roles || [];

    return {
        roles: roles,
        valid: roles.length > 0,
    }
};

const EditTypePage = (props) => {
    const { snackbar, match, apps, accounts, types : allTypes, filters, onSave, onPrev, onNext } = props;
    const [ account, setAccount ] = useState({})
    const [ type, setType ] = useState({})
    const [ data, setData ] = useState(toData({}));
    const [ roles, setRoles ] = useState(toRoles({}));
    const [ saving, setSaving ] = useState(false);
    const [ prevId, setPrevId ] = useState(null);
    const [ nextId, setNextId ] = useState(null);

    const types = useMemo(() => {
        return allTypes.filter((type) => type.accountId === account.id);
    }, [allTypes, account]);

    const filtered = useFiltered(types, filters, sorting.sortByTitle);

    const app = useMemo(() => {
        return account ? apps.find(candidate => candidate.id === account.appId) : null;
    }, [apps, account]);

    const available = useMemo(() => {
        return app ? app.roles : [];
    }, [app]);

    const loadType = (typeId) => {
        const previousId = type.id;
        const found = arrays.findEntity(filtered, typeId) || {};
        const ids = arrays.getPrevNextIds(filtered, found);

        setType(found);
        setPrevId(ids.prevId);
        setNextId(ids.nextId);

        if (previousId && previousId !== typeId && found.id === typeId) {
            snackbar.setInfo("Loaded " + found.title);
        }
    };

    const redirectToPrev = () => {
        const accountId = match.params.accountId;

        onPrev(accountId, prevId);
        loadType(prevId);
    };

    const redirectToNext = () => {
        const accountId = match.params.accountId;

        onNext(accountId, nextId);
        loadType(nextId);
    };

    useEffect(() => {
        const accountId = match.params.accountId;
        const typeId = match.params.typeId;

        loadType(typeId);
        setAccount(arrays.findEntity(accounts, accountId));
    }, [filtered]);

    useEffect(() => {
        setData(toData(type));
        setRoles(toRoles(type));
    }, [type]);

    const resetForm = () => {
        setData(toData(type));
        setRoles(toRoles(type));
    };

    const cleanForm = () => {
        setData(forms.cleanData(data));
        setRoles(forms.cleanData(roles));
    };

    const submitForm = () => {
        setSaving(true);

        const modified = {
            title: data.title,
            description: data.description,
            roles: roles.roles,
        };

        const success = (type) => {
            cleanForm();
            setSaving(false);

            snackbar.setSuccess("Type saved");
            setType(type);
            onSave(type);
        };

        const failure = (err) => {
            setSaving(false);
            snackbar.setError(err);
        };

        internalTypeService.saveType(account.id, type.id, modified, success, failure);
    };

    const valid = data.valid;
    const modified = forms.differ(toData(type), data) || forms.differ(toRoles(type), roles);

    return saving ?
        <div className="edit-type-page">
            <CircularProgress size="80px"/>
        </div> :
        <div className="edit-type-page">
            {account ?
                <>
                    <AccountAvatar
                        account={account}
                        mode="no-title"
                        size={account.branding && account.branding.logoContainsName ? "plus" : "medium"}
                    />
                    {account.branding && !account.branding.logoContainsName ?
                        <Typography variant="caption">
                            {account.name}
                        </Typography> : null}
                    <Divider/>
                </> : null}
            <div className="cards">
                <div className="card">
                    <TypeForm
                        data={data}
                        onChange={setData}
                    />
                </div>
                <div className="card">
                    <TypeRoleForm
                        roles={available}
                        data={roles}
                        onChange={setRoles}
                    />
                </div>
            </div>
            <div className="actions">
                <IconButton
                    color="secondary"
                    title="Previous type"
                    disabled={!prevId}
                    onClick={redirectToPrev}
                >
                    <PrevIcon/>
                </IconButton>
                <IconButton
                    color="secondary"
                    title="Next type"
                    disabled={!nextId}
                    onClick={redirectToNext}
                >
                    <NextIcon/>
                </IconButton>
                <VerticalDivider/>
                <Button
                    color="secondary"
                    variant="text"
                    disabled={!modified}
                    onClick={resetForm}
                >
                    Undo
                </Button>
                <Button
                    color="primary"
                    size="large"
                    disabled={!valid || !modified || !type.id}
                    onClick={submitForm}
                >
                    Save
                </Button>
            </div>
            <Prompt when={modified} message={verbiage.UNSAVED_CHANGES}/>
        </div>
}

export default EditTypePage;
